<template>
  <div class="products-table-outer">
    <div class="blank"></div>
    <div class="table-wrapper">
      <div v-if="totalProducts === 0">
        Opportunity data not available
      </div>
      <div class="table-wrapper-inner" v-else>
        <span class="title">Top {{ portfolioName }} Opportunities</span>
        <div
          ref="tableElement"
          class="table"
          :class="{ glow: props.glow }"
          :style="{ '--color': props.color }"
        >
          <div class="table-head">
            <template v-for="(header, index) in headers" :key="index">
              <div
                class="head-item"
                :style="{ width: header.toLowerCase() === 'sku' ? '40%' : '10%' }"
              >
                {{ formatHeader(header) }}
              </div>
              <div v-if="index !== headers.length - 1" class="separator"></div>
            </template>
          </div>
          <div class="table-body">
            <div class="table-row" v-for="(row, rowIndex) in paginatedData" :key="rowIndex">
              <template v-for="(header, colIndex) in headers" :key="colIndex">
                <div
                  class="table-cell"
                  :style="{
                    color:
                      header === 'loyalty' || header === 'substitution' || header === 'distribution' || header === 'franchiseShare'
                        ? props.color
                        : '',
                    width: header.toLowerCase() === 'sku' ? '40%' : '10%'
                  }"
                >
                  {{ formatCellValue(header, row[header]) }}
                </div>
                <div v-if="colIndex !== headers.length - 1"></div>
              </template>
            </div>
          </div>
        </div>
        <!-- Pagination Controls -->
        <div class="pagination">
          <button
            v-for="page in displayedPages"
            :key="page"
            :disabled="page === '...'"
            :class="{ active: page === currentPage }"
            @click="goToPage(page)"
          >
            {{ page === '...' ? '...' : `Page ${page}` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatCurrency, formatNumber, formatPercent } from '@/components/common/common'
import type { OpportunityProducts } from '@/data/dbghub'
import { computed, ref, watch, onUnmounted, onMounted, onBeforeMount, nextTick } from 'vue'

interface IProps {
  data: OpportunityProducts | null | undefined
  portfolioName: string
  color?: string
  glow?: boolean
}

const props = defineProps<IProps>()

const currentPage = ref(1)
const rowsPerPage = ref(8)

// Reference to the table element
const tableElement = ref<HTMLElement | null>(null)

const getMinRowHeightPx = () => {
  const remHeight = 4.6875 // 75px at 16px base
  if (typeof window === 'undefined') return remHeight * 16
  const base = parseFloat(getComputedStyle(document.documentElement).fontSize || '16')
  return remHeight * base
}

const updateRowsPerPage = () => {
  if (!tableElement.value) return

  const tableHeight = tableElement.value.clientHeight

  const minRowHeightPx = getMinRowHeightPx()
  const newRowsPerPage = Math.floor(tableHeight / minRowHeightPx)

  rowsPerPage.value = Math.max(newRowsPerPage, 1)
}

// Handle window resize (Zoom In/Out)
const handleResize = () => {
  updateRowsPerPage()
}

onBeforeMount(() => {
  window.addEventListener('resize', handleResize)
})

onMounted(async () => {
  await nextTick(); // Ensures Vue has fully rendered the DOM before calculations
  updateRowsPerPage();
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

watch(
  () => props.data,()=>{
    currentPage.value = 1; // Reset pagination on data change

    if (currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value || 1;
    }

    updateRowsPerPage(); // Update table row calculations
  }
);

// Dynamically computed headers
const headers = computed(() => {
  return props.data && props.data.length > 0 ? Object.keys(props.data[0]!) : []
})

// Computed for pagination
const totalProducts = computed(() => {
  if (!props.data) return 0
  return props.data.length
})

const totalPages = computed(() => {
  if (!props.data) return 0
  return Math.ceil(props.data.length / rowsPerPage.value)
})

// Paginated Data
const paginatedData = computed(() => {
  if (!props.data) return []
  if (props.data.length === 0) return []

  // Sort by opportunityValue in descending order
  const sortedData = [...props.data].sort((a, b) => {
    const valueA = a.opportunityValue || 0
    const valueB = b.opportunityValue || 0
    return valueB - valueA
  })

  // Pagination logic
  const start = (currentPage.value - 1) * rowsPerPage.value
  const paginated = sortedData.slice(start, start + rowsPerPage.value)

  return paginated
})

// Pagination buttons
const displayedPages = computed(() => {
  if (totalPages.value) {
    const pages: (number | string)[] = []
    const lastPage = totalPages.value

    if (lastPage <= 4) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage.value < 3) {
        pages.push(1, 2, 3, '...', lastPage)
      } else if (currentPage.value > lastPage - 2) {
        pages.push(1, '...', lastPage - 2, lastPage - 1, lastPage)
      } else {
        pages.push(
          1,
          '...',
          currentPage.value - 1,
          currentPage.value,
          currentPage.value + 1,
          '...',
          lastPage
        )
      }
    }

    return pages
  }
  return []
})

// Formatters
const formatCellValue = (key: string, value: any): string => {
  if (
    key.toLowerCase() === 'substitution' ||
    key.toLowerCase() === 'loyalty' ||
    key.toLowerCase() === 'distribution' ||
    key.toLowerCase() === 'franchiseshare'
  ) {
    return formatPercent(value, 1)
  } else if (key.toLowerCase() === 'opportunityvalue') {
    return formatCurrency(value)
  } else if (key.toLowerCase() === 'opportunityunits') {
    return formatNumber(value)
  }
  return value
}

// Capitalize header for better readability
const formatHeader = (header: string): string => {
  return header.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (str) => str.toUpperCase())
}

const goToPage = (page: number | string) => {
  if (typeof page === 'number') {
    currentPage.value = page
  }
}
</script>

<style scoped>
.products-table-outer {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5%;
}

.blank {
  height: 3dvh;
}

.table-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-secondary);
  color: var(--color-background-bright);
  transition:
    background-color 0.5s ease,
    flex 0.5s ease;
  width: 100%;
  height: 100%;
  border-radius: 6.25rem;
  padding-block: 5%;
  min-width: 37.5rem;
  min-height: 52.25rem;
}

.table-wrapper-inner {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
}

.title {
  font-size: var(--font-size-h2);
  font-weight: 600;
  height: 10%;
}

.table {
  width: 100%;
  height: 80%;
  position: relative;
}

.table .table-head {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-around;
  font-size: var(--font-size-caption);
  font-weight: 400;
  color: var(--color-background-bright-80);
  text-align: center;
}

.table .table-head .separator::after {
  content: '';
  width: 0.03125rem;
  height: 100%;
  background-color: var(--color-background-bright-90);
  top: 0;
  position: absolute;
}

.table-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  height: 100%;
}

.table .table-body .table-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-around;
  text-align: center;
  min-height: 4.6875rem;
  font-size: var(--font-size-table);
}

.table .table-body .table-row:nth-child(even) {
  background-color: var(--color-background-bright-5);
}

.pagination {
  margin-top: 0.625rem;
  display: flex;
  justify-content: center;
  gap: 0.625rem;
  height: 10%;
}
.pagination button {
  padding: 0.3125rem 0.625rem;
  cursor: pointer;
  font-size: var(--font-size-caption);
  color: var(--color-background-bright-80);
}
.pagination button.active {
  color: var(--color-background-bright);
}
.pagination button:hover {
  color: var(--color-background-bright);
}

.table.glow:before {
  content: '';
  width: 50%;
  aspect-ratio: 1;
  background-color: var(--color);
  position: absolute;
  top: -26%;
  left: -7.5%;
  z-index: -1;
  border-radius: 60%;
  filter: blur(2.5rem);
  opacity: 0.5;
}

.table.glow:after {
  content: '';
  width: 50%;
  aspect-ratio: 1;
  background-color: var(--color);
  position: absolute;
  bottom: -31.5%;
  right: -7.5%;
  z-index: -1;
  border-radius: 60%;
  filter: blur(2.5rem);
  opacity: 0.5;
}
</style>
